
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function SMSSignupSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    serviceInterest: '',
    smsOptIn: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d3jsmtvb6m158lvbnab0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          serviceInterest: '',
          smsOptIn: true
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Stay Connected with SMS Updates
            </h2>
            <p className="text-xl text-orange-100 mb-2">
              Get instant notifications about appointments, cleaning tips, and exclusive promotions
            </p>
            <p className="text-lg text-orange-200">
              Join hundreds of satisfied customers who stay updated via SMS
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <i className="ri-message-3-line text-orange-500 text-2xl"></i>
              </div>
              <div className="text-left">
                <h3 className="text-2xl font-bold text-gray-800">SMS Notifications</h3>
                <p className="text-gray-600">Quick updates, right to your phone</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="sms-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="sms-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label htmlFor="sms-phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="sms-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="sms-service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Interest
                </label>
                <select
                  id="sms-service"
                  name="serviceInterest"
                  value={formData.serviceInterest}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select your interest (optional)</option>
                  <option value="residential">Residential Cleaning</option>
                  <option value="commercial">Commercial Cleaning</option>
                  <option value="deep-cleaning">Deep Cleaning</option>
                  <option value="move-in-out">Move In/Out Cleaning</option>
                  <option value="post-construction">Post-Construction Cleanup</option>
                  <option value="carpet-cleaning">Carpet Cleaning</option>
                  <option value="window-cleaning">Window Cleaning</option>
                  <option value="all-services">All Services</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="sms-optIn"
                    name="smsOptIn"
                    checked={formData.smsOptIn}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded cursor-pointer"
                  />
                  <div className="flex-1">
                    <label htmlFor="sms-optIn" className="text-sm font-medium text-gray-700 cursor-pointer">
                      SMS Opt In
                    </label>
                    <p className="text-sm text-gray-600 mt-1 mb-3">
                      Opt in to receive SMS notifications, alerts & occasional marketing communication.
                    </p>
                    <div className="text-xs text-gray-500 leading-relaxed space-y-2">
                      <p>
                        By providing your phone number, you agree to receive text messages from Signature Cleaning Services about appointments, updates, and promotions.
                      </p>
                      <p>
                        We won't share your info with third parties for marketing.
                      </p>
                      <p>
                        Message frequency may vary (up to 4–6 messages/month).
                      </p>
                      <p>
                        Reply STOP to unsubscribe at any time.
                      </p>
                      <p>
                        Reply HELP for more info.
                      </p>
                      <p>
                        Message & data rates may apply.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                    <p className="text-green-700">Welcome! You're now signed up for SMS updates. You'll receive a confirmation text shortly.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                    <p className="text-red-700">Sorry, there was an error with your signup. Please try again or call us directly.</p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full md:w-auto bg-orange-500 hover:bg-orange-600"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Signing Up...
                    </>
                  ) : (
                    <>
                      <i className="ri-smartphone-line mr-2"></i>
                      Sign Up for SMS Updates
                    </>
                  )}
                </Button>
              </div>

              <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mt-6">
                <div className="flex items-center">
                  <i className="ri-shield-check-line text-green-500 mr-2"></i>
                  Secure & Private
                </div>
                <div className="flex items-center">
                  <i className="ri-time-line text-blue-500 mr-2"></i>
                  Instant Updates
                </div>
                <div className="flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-2"></i>
                  Easy Unsubscribe
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
